import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useIsAdmin } from "@/lib/portal/useIsAdmin";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/portal/admin/tickets")({
  component: AdminTickets,
});

type Ticket = {
  id: string; code: string; title: string; description: string | null;
  category: string; priority: string; status: string;
  assigned_developer: string | null; progress: number;
  created_at: string; project_id: string; created_by: string;
};

const STATUSES = ["open", "in_progress", "waiting", "testing", "completed"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-500/20 text-blue-300",
  waiting: "bg-amber-500/20 text-amber-300",
  in_progress: "bg-fuchsia-500/20 text-fuchsia-300",
  testing: "bg-cyan-500/20 text-cyan-300",
  completed: "bg-emerald-500/20 text-emerald-300",
};

function AdminTickets() {
  const { data: isAdmin, isLoading } = useIsAdmin();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [active, setActive] = useState<Ticket | null>(null);

  const { data: tickets } = useQuery({
    queryKey: ["admin", "all-tickets"],
    enabled: !!isAdmin,
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data } = await supabase.from("tickets").select("*").order("created_at", { ascending: false });
      return (data ?? []) as Ticket[];
    },
  });

  const { data: projects } = useQuery({
    queryKey: ["admin", "projects-lookup"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const { data } = await supabase.from("projects").select("id, name, owner_id");
      return data ?? [];
    },
  });

  const projMap = useMemo(() => Object.fromEntries((projects ?? []).map((p) => [p.id, p])), [projects]);

  const filtered = useMemo(() => {
    return (tickets ?? []).filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      if (query && !t.title.toLowerCase().includes(query.toLowerCase()) && !t.code.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [tickets, statusFilter, priorityFilter, query]);

  if (isLoading) return <div className="text-white/60 text-sm">Verifica accesso…</div>;
  if (!isAdmin) throw redirect({ to: "/portal" });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Inbox ticket globale</h1>
        <p className="text-white/60 mt-1">Tutti i ticket di tutti i clienti in un unico posto.</p>
      </div>

      {/* Filters */}
      <div className={`${glass} p-4 flex flex-wrap items-center gap-3`}>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca per titolo o codice…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm" />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Filter className="h-4 w-4 text-white/40" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg bg-white/5 border border-white/10 px-2 py-2 text-xs">
            <option value="all" className="bg-black">Tutti gli stati</option>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-black">{s.replace("_", " ")}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="rounded-lg bg-white/5 border border-white/10 px-2 py-2 text-xs">
            <option value="all" className="bg-black">Tutte priorità</option>
            {PRIORITIES.map((p) => <option key={p} value={p} className="bg-black">{p}</option>)}
          </select>
        </div>
        <div className="text-xs text-white/50">{filtered.length} risultati</div>
      </div>

      {/* Table */}
      <div className={`${glass} overflow-hidden`}>
        <div className="hidden md:grid grid-cols-[90px_1fr_180px_100px_100px_120px_100px] px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
          <div>Codice</div><div>Titolo</div><div>Cliente</div><div>Priorità</div><div>Progress</div><div>Status</div><div>Data</div>
        </div>
        {filtered.map((t) => {
          const proj = projMap[t.project_id];
          return (
            <button key={t.id} onClick={() => setActive(t)}
              className="w-full md:grid md:grid-cols-[90px_1fr_180px_100px_100px_120px_100px] flex flex-col text-left items-start md:items-center gap-1 px-4 py-3 text-sm border-b border-white/5 last:border-b-0 hover:bg-white/[0.03]">
              <div className="font-mono text-white/60 text-xs">{t.code}</div>
              <div className="font-medium truncate pr-2 w-full">{t.title}</div>
              <div className="text-white/60 text-xs truncate">{proj?.name ?? "—"}</div>
              <div className={`text-xs ${t.priority === "urgent" ? "text-red-400 font-semibold" : t.priority === "high" ? "text-orange-400" : "text-white/60"}`}>{t.priority}</div>
              <div className="w-full max-w-[80px]">
                <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${t.progress}%` }} />
                </div>
              </div>
              <div><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${STATUS_COLORS[t.status] ?? "bg-white/5"}`}>{t.status.replace("_", " ")}</span></div>
              <div className="text-[11px] text-white/40">{format(new Date(t.created_at), "d MMM HH:mm", { locale: it })}</div>
            </button>
          );
        })}
        {filtered.length === 0 && <div className="p-12 text-center text-sm text-white/40">Nessun ticket con questi filtri</div>}
      </div>

      <AnimatePresence>
        {active && <AdminTicketDetail ticket={active} projectName={projMap[active.project_id]?.name} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}

function AdminTicketDetail({ ticket, projectName, onClose }: { ticket: Ticket; projectName?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState(ticket.status);
  const [priority, setPriority] = useState(ticket.priority);
  const [assigned, setAssigned] = useState(ticket.assigned_developer ?? "");
  const [progress, setProgress] = useState(ticket.progress);

  const { data: msgs } = useQuery({
    queryKey: ["admin", "ticket-msgs", ticket.id],
    queryFn: async () => (await supabase.from("ticket_messages").select("*").eq("ticket_id", ticket.id).order("created_at")).data ?? [],
    refetchInterval: 8000,
  });

  const send = async () => {
    if (!msg.trim()) return;
    const { data: user } = await supabase.auth.getUser();
    const { error } = await supabase.from("ticket_messages").insert({
      ticket_id: ticket.id, sender_id: user.user!.id, sender_role: "admin", body: msg,
    });
    if (error) return toast.error(error.message);
    setMsg("");
    qc.invalidateQueries({ queryKey: ["admin", "ticket-msgs", ticket.id] });
  };

  const saveMeta = async () => {
    const { error } = await supabase.from("tickets").update({
      status, priority, assigned_developer: assigned || null, progress,
    }).eq("id", ticket.id);
    if (error) return toast.error(error.message);
    toast.success("Ticket aggiornato");
    qc.invalidateQueries({ queryKey: ["admin", "all-tickets"] });

    // Notifica cliente
    await supabase.from("notifications").insert({
      user_id: ticket.created_by,
      title: `Ticket ${ticket.code} aggiornato`,
      body: `Nuovo stato: ${status.replace("_", " ")}${assigned ? ` • Assegnato a ${assigned}` : ""}`,
      link: "/portal/tickets",
      type: "ticket_update",
    });
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl p-6 rounded-3xl bg-[#0f0f16] border border-white/10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-xs font-mono text-white/50">{ticket.code} · {projectName ?? "—"}</div>
            <h3 className="font-semibold text-xl mt-1">{ticket.title}</h3>
            {ticket.description && <p className="text-sm text-white/60 mt-2">{ticket.description}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        {/* Meta controls */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <MetaField label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1.5">
              {STATUSES.map((s) => <option key={s} value={s} className="bg-black">{s.replace("_", " ")}</option>)}
            </select>
          </MetaField>
          <MetaField label="Priorità">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1.5">
              {PRIORITIES.map((p) => <option key={p} value={p} className="bg-black">{p}</option>)}
            </select>
          </MetaField>
          <MetaField label="Developer">
            <input value={assigned} onChange={(e) => setAssigned(e.target.value)} placeholder="Nome"
              className="w-full text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1.5" />
          </MetaField>
          <MetaField label={`Progress ${progress}%`}>
            <input type="range" min={0} max={100} value={progress} onChange={(e) => setProgress(Number(e.target.value))} className="w-full" />
          </MetaField>
        </div>
        <button onClick={saveMeta} className="w-full py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black text-sm font-semibold mb-6">
          Salva modifiche e notifica cliente
        </button>

        {/* Messages */}
        <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Conversazione</div>
        <div className="max-h-60 overflow-y-auto space-y-2 mb-3 pr-1">
          {(msgs ?? []).map((m) => (
            <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender_role === "admin" ? "bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-black" : "bg-white/5 border border-white/10"}`}>
                <div className="text-[9px] uppercase opacity-70 mb-0.5">{m.sender_role}</div>
                {m.body}
              </div>
            </div>
          ))}
          {(msgs ?? []).length === 0 && <div className="text-center text-xs text-white/40 py-6">Nessun messaggio ancora</div>}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Rispondi al cliente…"
            className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          <button className="p-2 rounded-xl bg-white text-black"><Send className="h-4 w-4" /></button>
        </form>
      </motion.div>
    </>
  );
}

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2">
      <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}
